import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, catchError, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LoggerService } from '../logging/logger.service';

export interface AuthUser {
  sub: string;
  email: string;
  name: string;
  uid: string;        // SSO / Mashery user id
  firstName: string;
  isAdmin: boolean;
  groups: string[];
}

const PKCE_VERIFIER_KEY = 'mfr_pkce_verifier';
const OAUTH_STATE_KEY = 'mfr_oauth_state';

/** Mock user used only when bypassAuth=true (local development). */
const LOCAL_MOCK_USER: AuthUser = {
  sub: 'local-dev-user',
  email: 'saahil.bhutwala@nbcuni.com',
  name: 'Saahil Bhutwala',
  uid: '206777181',
  firstName: 'Saahil',
  isAdmin: true,
  groups: ['mfr-admins'],
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly logger = inject(LoggerService);

  // In-memory only — never persisted to localStorage
  private _token: string | null = null;
  private _user = signal<AuthUser | null>(null);

  // Resolves once startup auth work (the Entra code exchange) has settled.
  private _ready: Promise<void> = Promise.resolve();
  private _loginFailed = false;

  readonly currentUser = this._user.asReadonly();
  readonly isAuthenticated = signal(false);

  /** Registered by initializeAuth so guards can await an in-flight code exchange. */
  setReady(ready: Promise<void>): void {
    this._ready = ready;
  }

  whenReady(): Promise<void> {
    return this._ready;
  }

  /** True when a returned `?code=` was rejected — retrying would just loop. */
  loginFailed(): boolean {
    return this._loginFailed;
  }

  markLoginFailed(): void {
    this._loginFailed = true;
  }

  getToken(): string | null {
    return this._token;
  }

  /**
   * Called once on app startup when bypassAuth=true.
   * Sets a mock user so all guards pass without a real Entra token.
   */
  initLocalBypass(): void {
    this._token = 'local-dev-bypass-token';
    this._user.set(LOCAL_MOCK_USER);
    this.isAuthenticated.set(true);
    this.logger.info('LocalAuthBypass', { user: LOCAL_MOCK_USER.email });
  }

  /**
   * Build the Entra ID authorize URL (OIDC authorization code flow + PKCE)
   * and redirect the browser to it.
   *
   * `state` is generated here and re-checked on the way back to defend against
   * CSRF / authorization-code-injection.
   */
  async initiateLogin(): Promise<never> {
    const verifier = this._generateVerifier();
    const challenge = await this._deriveChallenge(verifier);
    const state = this._generateVerifier();

    sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
    sessionStorage.setItem(OAUTH_STATE_KEY, state);

    const params = new URLSearchParams({
      client_id: environment.entraClientId,
      response_type: 'code',
      response_mode: 'query',
      redirect_uri: environment.redirectUri,
      scope: environment.entraScopes,
      state,
      code_challenge: challenge,
      code_challenge_method: 'S256',
    });

    window.location.href = `${environment.entraAuthorizeUrl}?${params.toString()}`;
    // Never returns
    return new Promise(() => {});
  }

  /**
   * Exchange the Entra authorization code for tokens via the API backend.
   *
   * The backend performs the Entra token_endpoint POST using the code_verifier
   * plus the confidential-client secret (held in Secrets Manager), reads the
   * user's SSO id off the id_token's UPN claim, and returns that id_token.
   * That id_token is what the API Gateway JWT authorizer validates on every
   * subsequent request.
   */
  exchangeCode(code: string, state?: string | null): Observable<{ token: string; user: AuthUser }> {
    const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY) ?? '';
    const expectedState = sessionStorage.getItem(OAUTH_STATE_KEY);
    sessionStorage.removeItem(PKCE_VERIFIER_KEY);
    sessionStorage.removeItem(OAUTH_STATE_KEY);

    if (expectedState && state !== expectedState) {
      this.logger.error('AuthStateMismatch', new Error('OAuth state parameter mismatch'));
      return throwError(() => new Error('Invalid authentication state'));
    }

    return this.http
      .post<{ token: string; user: AuthUser }>(`${environment.apiUrl}/Report/Token/callback`, {
        code,
        code_verifier: verifier,
        redirect_uri: environment.redirectUri,
      })
      .pipe(
        tap((response) => {
          this._token = response.token;
          this._user.set(response.user);
          this.isAuthenticated.set(true);
          this.logger.info('AuthSuccess', { userId: response.user.sub });
        }),
        catchError((err) => {
          this.logger.error('AuthFailed', err);
          return throwError(() => err);
        })
      );
  }

  logout(): void {
    this._token = null;
    this._user.set(null);
    this.isAuthenticated.set(false);
    sessionStorage.clear();
    this.logger.info('UserLoggedOut');
    if (environment.bypassAuth) {
      // In local mode just re-apply the bypass instead of redirecting to Entra
      this.initLocalBypass();
      return;
    }
    window.location.href = environment.entraLogoutUrl;
  }

  isAdmin(): boolean {
    return this._user()?.isAdmin ?? false;
  }

  // ── PKCE helpers ─────────────────────────────────────────────────────────

  private _generateVerifier(): string {
    const array = new Uint8Array(96);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode(...array))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  private async _deriveChallenge(verifier: string): Promise<string> {
    const encoded = new TextEncoder().encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', encoded);
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }
}
